import React, { useEffect, useRef, useState } from 'react'
import { supabase } from '../supabaseClient.js'

const CTO_EMAIL = 'advdeepaknarwal8@gmail.com'
const PAGE_SIZE = 1000
const POLL_INTERVAL_MS = 15000
const Fragment = React.Fragment

function normalizeEmail(value) {
  return String(value ?? '').trim().toLowerCase()
}

function isAdminEmail(value) {
  return normalizeEmail(value) === CTO_EMAIL
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') {
    return 0
  }

  const numericValue = Number(String(value).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(numericValue) ? numericValue : 0
}

function formatCurrency(value) {
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0,
  }).format(toNumber(value))
}

function formatDate(value) {
  if (!value) {
    return 'Date unavailable'
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return 'Date unavailable'
  }

  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function normalizeRecord(value) {
  if (!value) {
    return {}
  }

  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch {
      return {}
    }
  }

  if (typeof value === 'object') {
    return value
  }

  return {}
}

function getCustomerName(order) {
  const shippingAddress = normalizeRecord(order?.shipping_address)
  const rawName =
    shippingAddress.fullName ||
    shippingAddress.full_name ||
    order?.customer_name ||
    order?.name ||
    order?.email ||
    ''

  return String(rawName).trim() || 'Unknown'
}

function getAddressLabel(order) {
  const shippingAddress = normalizeRecord(order?.shipping_address)
  const addressLine =
    shippingAddress.addressLine ||
    shippingAddress.address_line ||
    order?.address ||
    shippingAddress.address ||
    ''
  const state = order?.state || shippingAddress.state || ''
  const pincode = order?.pincode || shippingAddress.pincode || ''
  const addressParts = [addressLine, state].filter(Boolean)

  if (!addressParts.length && !pincode) {
    return 'Address unavailable'
  }

  const combinedAddress = addressParts.join(', ')

  if (combinedAddress && pincode) {
    return `${combinedAddress} - ${pincode}`
  }

  return combinedAddress || pincode
}

function getCustomerContactLabel(order) {
  const shippingAddress = normalizeRecord(order?.shipping_address)
  const email = String(
    shippingAddress.email || order?.email || order?.customer_email || '',
  ).trim()
  const phone = String(shippingAddress.phone || order?.phone || '').trim()
  const contactParts = [email, phone].filter(Boolean)

  return contactParts.join(' | ')
}

function getOrderAmount(order) {
  return toNumber(
    order?.total_price ??
      order?.price ??
      order?.amount_total ??
      order?.amount ??
      order?.total_amount ??
      order?.product_price,
  )
}

function getOrderStatus(order) {
  const paymentStatus = String(
    order?.payment_status || order?.status || 'unknown',
  ).replace(/_/g, ' ')
  const fulfillmentStatus = String(order?.fulfillment_status || '').replace(
    /_/g,
    ' ',
  )

  return fulfillmentStatus
    ? `${paymentStatus} / ${fulfillmentStatus}`
    : paymentStatus
}

function getFulfillmentStatusLabel(order) {
  return String(order?.fulfillment_status || 'Pending').replace(/_/g, ' ')
}

function getQikinkReferenceLabel(order) {
  const qikinkOrderId = String(order?.qikink_order_id || '').trim()
  const trackingId = String(order?.tracking_id || '').trim()

  return qikinkOrderId || trackingId
}

function getStoreSkuLabel(order) {
  return String(order?.store_sku || order?.sku || '').trim()
}

function formatSyncTime(value) {
  if (!value) {
    return ''
  }

  const parsedDate = new Date(value)

  if (Number.isNaN(parsedDate.getTime())) {
    return ''
  }

  return parsedDate.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

function StatCard({ label, value, caption }) {
  return (
    <article className="border border-black/10 bg-white px-5 py-4 text-black">
      <p className="text-[10px] uppercase tracking-[0.28em] text-black/40">
        {label}
      </p>
      <p className="mt-4 text-3xl font-semibold tracking-[-0.06em] text-black">
        {value}
      </p>
      {caption ? (
        <p className="mt-2 text-xs uppercase tracking-[0.2em] text-black/40">
          {caption}
        </p>
      ) : null}
    </article>
  )
}

async function fetchAllRows(tableName, orderColumn) {
  if (!supabase?.from) {
    return []
  }

  let rows = []
  let start = 0
  let shouldUseOrder = Boolean(orderColumn)

  while (true) {
    let query = supabase.from(tableName).select('*')

    if (shouldUseOrder && orderColumn) {
      query = query.order(orderColumn, { ascending: false })
    }

    const { data, error } = await query.range(start, start + PAGE_SIZE - 1)

    if (error) {
      if (shouldUseOrder) {
        shouldUseOrder = false
        rows = []
        start = 0
        continue
      }

      throw error
    }

    const batch = data ?? []
    rows.push(...batch)

    if (batch.length < PAGE_SIZE) {
      break
    }

    start += PAGE_SIZE
  }

  return rows
}

async function fetchGlobalOrders() {
  if (!supabase?.from) {
    return []
  }

  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .order('created_at', { ascending: false })

  if (error) {
    console.error('CTO Fetch Error (Check RLS):', error)
    throw error
  }

  if (Array.isArray(data) && data.length >= PAGE_SIZE) {
    return fetchAllRows('orders', 'created_at')
  }

  return Array.isArray(data) ? data : []
}

function AdminHQ({ onClose = () => {} }) {
  const [globalOrders, setGlobalOrders] = useState([])
  const [bags, setBags] = useState([])
  const [loading, setLoading] = useState(true)
  const [authChecking, setAuthChecking] = useState(true)
  const [isAuthorized, setIsAuthorized] = useState(false)
  const [accessMessage, setAccessMessage] = useState('')
  const [lastSyncedAt, setLastSyncedAt] = useState('')
  const [ledgerError, setLedgerError] = useState('')
  const activeRequestRef = useRef(0)

  useEffect(() => {
    let isActive = true
    let authSubscription = null

    async function syncAccessState() {
      if (!supabase?.auth?.getSession) {
        if (isActive) {
          setIsAuthorized(false)
          setAccessMessage('Supabase auth is unavailable.')
          setAuthChecking(false)
        }

        return
      }

      try {
        const { data, error } = await supabase.auth.getSession()

        if (error) {
          throw error
        }

        const session = data?.session ?? null
        const allowed = isAdminEmail(session?.user?.email)

        if (isActive) {
          setIsAuthorized(allowed)
          setAccessMessage(
            allowed
              ? ''
              : 'CTO Dashboard access is restricted to the approved account.',
          )
        }
      } catch (error) {
        if (isActive) {
          console.error('Failed to verify CTO access.', error)
          setIsAuthorized(false)
          setAccessMessage('Unable to verify CTO access right now.')
        }
      } finally {
        if (isActive) {
          setAuthChecking(false)
        }
      }
    }

    void syncAccessState()

    if (supabase?.auth?.onAuthStateChange) {
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        if (!isActive) {
          return
        }

        const allowed = isAdminEmail(session?.user?.email)
        setIsAuthorized(allowed)
        setAccessMessage(
          allowed
            ? ''
            : 'CTO Dashboard access is restricted to the approved account.',
        )
        setAuthChecking(false)
      })

      authSubscription = subscription
    }

    return () => {
      isActive = false

      if (authSubscription) {
        authSubscription.unsubscribe()
      }
    }
  }, [])

  useEffect(() => {
    if (authChecking) {
      return undefined
    }

    if (!isAuthorized) {
      setGlobalOrders([])
      setBags([])
      setLedgerError('')
      setLastSyncedAt('')
      setLoading(false)
      return undefined
    }

    let isActive = true
    let refreshTimer = null

    async function loadLedger({ showLoading = false } = {}) {
      const requestToken = activeRequestRef.current + 1
      activeRequestRef.current = requestToken

      if (showLoading && isActive) {
        setLoading(true)
      }

      try {
        const [ordersResult, bagsResult] = await Promise.allSettled([
          fetchGlobalOrders(),
          fetchAllRows('bags_dropped', 'created_at'),
        ])

        if (!isActive || activeRequestRef.current !== requestToken) {
          return
        }

        if (ordersResult.status === 'rejected') {
          throw ordersResult.reason
        }

        if (bagsResult.status === 'rejected') {
          console.error('Failed to load bags for CTO HQ:', bagsResult.reason)
        }

        setGlobalOrders(ordersResult.value)
        setBags(bagsResult.status === 'fulfilled' ? bagsResult.value : [])
        setLedgerError('')
        setLastSyncedAt(new Date().toISOString())
      } catch (error) {
        if (isActive) {
          console.error('Failed to load CTO HQ data:', error)
          setGlobalOrders([])
          setBags([])
          setLedgerError(
            error instanceof Error
              ? error.message
              : 'Unable to load the CTO ledger right now.',
          )
        }
      } finally {
        if (isActive && showLoading) {
          setLoading(false)
        }
      }
    }

    void loadLedger({ showLoading: true })

    refreshTimer = window.setInterval(() => {
      void loadLedger({ showLoading: false })
    }, POLL_INTERVAL_MS)

    return () => {
      isActive = false

      if (refreshTimer) {
        window.clearInterval(refreshTimer)
      }
    }
  }, [authChecking, isAuthorized])

  const totalOrders = globalOrders?.length || 0
  const totalRevenue =
    globalOrders?.reduce((acc, order) => {
      const amount = Number(
        order?.total_price ||
          order?.price ||
          order?.amount_total ||
          order?.amount ||
          order?.total_amount ||
          order?.product_price ||
          0,
      )

      return acc + (Number.isFinite(amount) ? amount : 0)
    }, 0) || 0
  const totalBags = bags.length

  if (authChecking) {
    return (
      <Fragment>
        <div className="fixed inset-0 z-[200] bg-white text-black">
          <div className="flex h-full items-center justify-center px-5">
            <div className="w-full max-w-md border border-black/10 bg-white p-6 text-center">
              <p className="text-[10px] uppercase tracking-[0.35em] text-black/35">
                Aiko Vibe
              </p>
              <h1 className="mt-3 text-2xl font-semibold tracking-[-0.06em] text-black">
                Verifying CTO access
              </h1>
              <p className="mt-4 text-sm leading-6 text-black/55">
                Checking the logged-in account before loading the private ledger.
              </p>
            </div>
          </div>
        </div>
      </Fragment>
    )
  }

  if (!isAuthorized) {
    return (
      <Fragment>
        <div className="fixed inset-0 z-[200] bg-white text-black">
          <div className="flex h-full items-center justify-center px-5">
            <div className="w-full max-w-md border border-black/10 bg-white p-6">
              <p className="text-[10px] uppercase tracking-[0.35em] text-black/35">
                Aiko Vibe
              </p>
              <h1 className="mt-3 text-2xl font-semibold tracking-[-0.06em] text-black">
                CTO Dashboard locked
              </h1>
              <p className="mt-4 text-sm leading-6 text-black/55">
                {accessMessage || 'Access is restricted to the approved account.'}
              </p>

              <button
                type="button"
                onClick={onClose}
                className="mt-6 text-sm font-semibold uppercase tracking-[0.22em] text-black transition-colors hover:text-black/50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </Fragment>
    )
  }

  return (
    <Fragment>
      <div className="fixed inset-0 z-[200] bg-white text-black">
        <div className="flex h-full flex-col overflow-y-auto">
          <header className="flex items-start justify-between border-b border-black/10 px-5 py-5 sm:px-8 lg:px-10">
            <div>
              <p className="text-[10px] uppercase tracking-[0.35em] text-black/35">
                Aiko Vibe
              </p>
              <h1
                id="cto-hq-title"
                className="mt-3 text-3xl font-semibold tracking-[-0.07em] text-black sm:text-4xl"
              >
                Aiko Vibe // CTO HQ
              </h1>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="text-sm font-semibold uppercase tracking-[0.22em] text-black transition-colors hover:text-black/50"
            >
              Close
            </button>
          </header>

          <main className="flex-1 px-5 py-6 sm:px-8 lg:px-10">
            <div className="mx-auto flex w-full max-w-7xl flex-col gap-8">
              {ledgerError ? (
                <section className="border border-black/10 bg-black/[0.02] px-4 py-3 text-sm leading-6 text-black/70">
                  {ledgerError}
                </section>
              ) : null}

              <section className="grid gap-3 md:grid-cols-3">
                <StatCard
                  label="Total Revenue (INR)"
                  value={`\u20B9${formatCurrency(totalRevenue)}`}
                  caption={
                    loading
                      ? 'syncing global ledger'
                      : lastSyncedAt
                        ? `last synced ${formatSyncTime(lastSyncedAt)}`
                        : 'all order rows'
                  }
                />
                <StatCard
                  label="Total Orders"
                  value={totalOrders.toString()}
                  caption={
                    loading
                      ? 'syncing global ledger'
                      : lastSyncedAt
                        ? `last synced ${formatSyncTime(lastSyncedAt)}`
                        : 'all order rows'
                  }
                />
                <StatCard
                  label="Total Bags dropped"
                  value={totalBags.toString()}
                  caption={loading ? 'syncing global ledger' : 'all bag rows'}
                />
              </section>

              <section className="space-y-4">
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.35em] text-black/35">
                      Orders Ledger
                    </p>
                    <h2 className="mt-2 text-2xl font-semibold tracking-[-0.06em] text-black">
                      All Orders
                    </h2>
                  </div>

                  <p className="text-xs uppercase tracking-[0.24em] text-black/35">
                    {loading
                      ? 'Loading all records'
                      : `${totalOrders} orders / ${totalBags} bags${
                          lastSyncedAt ? ` / synced ${formatSyncTime(lastSyncedAt)}` : ''
                        }`}
                  </p>
                </div>

                <div className="overflow-x-auto border border-black/10">
                  <table className="min-w-[980px] w-full border-collapse">
                    <thead className="border-b border-black/10 bg-white">
                      <tr className="text-left text-[10px] uppercase tracking-[0.28em] text-black/40">
                        <th className="px-4 py-4 font-semibold">Date</th>
                        <th className="px-4 py-4 font-semibold">Product</th>
                        <th className="px-4 py-4 font-semibold">Customer Name</th>
                        <th className="px-4 py-4 font-semibold">Address</th>
                        <th className="px-4 py-4 font-semibold">Amount</th>
                        <th className="px-4 py-4 font-semibold">Status</th>
                        <th className="px-4 py-4 font-semibold">Qikink</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-black/5 bg-white">
                      {loading ? (
                        <tr>
                          <td
                            colSpan={7}
                            className="px-4 py-10 text-center text-sm uppercase tracking-[0.22em] text-black/35"
                          >
                            Loading all records...
                          </td>
                        </tr>
                      ) : globalOrders.length ? (
                        globalOrders.map((order) => {
                          const customerName = getCustomerName(order)
                          const addressLabel = getAddressLabel(order)
                          const orderAmount = getOrderAmount(order)
                          const statusLabel = getOrderStatus(order)

                          return (
                            <tr
                              key={order?.id ?? `${order?.created_at ?? 'order'}-${customerName}`}
                              className="align-top transition-colors hover:bg-black/[0.02]"
                            >
                              <td className="px-4 py-4 text-sm text-black/70">
                                {formatDate(order?.created_at)}
                              </td>
                              <td className="px-4 py-4">
                                <div className="max-w-[280px]">
                                  <p className="truncate text-sm font-semibold text-black">
                                    {order?.product_name ||
                                      order?.product_title ||
                                      order?.title ||
                                      order?.name ||
                                      'Untitled vibe'}
                                  </p>
                                  {getStoreSkuLabel(order) ? (
                                    <p className="mt-1 text-[10px] uppercase tracking-[0.22em] text-black/35">
                                      SKU {getStoreSkuLabel(order)}
                                    </p>
                                  ) : null}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-black">
                                <div className="font-medium text-black">
                                  {customerName}
                                </div>
                                {getCustomerContactLabel(order) ? (
                                  <div className="mt-1 text-xs leading-5 text-black/45">
                                    {getCustomerContactLabel(order)}
                                  </div>
                                ) : null}
                              </td>
                              <td className="px-4 py-4 text-sm text-black/70">
                                {addressLabel}
                              </td>
                              <td className="px-4 py-4 text-sm font-semibold text-black">
                                {'\u20B9'}
                                {formatCurrency(orderAmount)}
                              </td>
                              <td className="px-4 py-4">
                                <span className="inline-flex rounded-full border border-black/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-black">
                                  {statusLabel}
                                </span>
                              </td>
                              <td className="px-4 py-4">
                                <div className="space-y-2">
                                  <span className="inline-flex whitespace-nowrap rounded-full border border-black/10 bg-black/[0.03] px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-black/60">
                                    Qikink: {getFulfillmentStatusLabel(order)}
                                  </span>

                                  {getQikinkReferenceLabel(order) ? (
                                    <div className="space-y-1 text-[10px] uppercase tracking-[0.22em] text-black/35">
                                      {order?.qikink_order_id ? (
                                        <p>
                                          Qikink ID: {String(order.qikink_order_id).trim()}
                                        </p>
                                      ) : null}
                                      {order?.tracking_id ? (
                                        <p>Tracking: {String(order.tracking_id).trim()}</p>
                                      ) : null}
                                    </div>
                                  ) : (
                                    <p className="text-[10px] uppercase tracking-[0.22em] text-black/30">
                                      Awaiting Qikink test sync
                                    </p>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan={7}
                            className="px-4 py-10 text-center text-sm uppercase tracking-[0.22em] text-black/35"
                          >
                            No orders found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          </main>
        </div>
      </div>
    </Fragment>
  )
}

export default AdminHQ
